import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCampaignContentBlocks, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import {
  CampaignContentBlock,
  CampaignContentBlockPayload,
  CampaignQuestionOption,
  StepQuestionRef,
} from '../../../../support/campaigns';
import { GhostButton } from './primitives';
import { ReferenceMultiSelect, ReferenceOption, ReferenceSelect } from './ReferenceSelect';
import { arrToLines, linesToArr, nextOrderOf } from './builderShared';
import { useCampaignCapabilityReference } from './builderReferences';
import { SlotAutocomplete } from './builderFields';

/** Cisco IQ scenario states the runner recognizes — fallback option set for the `when` gate when a
 *  campaign hasn't authored its own `iq_scenario` blocks (keep in sync with the runner's `IqScenario`). */
const IQ_SCENARIO_FALLBACK: ReferenceOption[] = [
  { value: 'active', label: 'Active (IQ connected)' },
  { value: 'enabled-unused', label: 'Enabled but unused' },
  { value: 'not-enabled', label: 'Not enabled' },
];

/**
 * Read/clean helpers + authoring panel for a step's PREDEFINED-QUESTION references — the exact analog of
 * `StepFilesFields` for questions. A step declares which of the campaign's question content blocks it
 * uses on `step.metadata.questionRefs` (a `StepQuestionRef[]`), pointing at each `CampaignContentBlock`
 * by (category, key). The question rows themselves stay campaign-level and reusable (created in the
 * Content Blocks section or inline here); a step only references them, so any runner (or the generic
 * step bodies) can read a step's questions and the same block can back many steps.
 *
 * Beyond the reference, the panel exposes a structured options/cues editor for the referenced block's
 * `payload` (`ask`/`why` + `CampaignQuestionOption[]` = `{ value, cues, plays }`), reusing the
 * one-per-line ergonomics of `QuestionSetEditor`. Those edits persist to the SHARED block via a
 * full-record PUT (`useCampaignMutations('contentBlock').update`) — not to the step — so repointing the
 * ref changes which question the step uses ("I just change the pointer") while the block's content is
 * authored once.
 */


/**
 * A content block is a "question" block when its payload carries an ask / options / who (a banded
 * question, e.g. question_bank or a deep-dive scenario question) OR a `question` prompt (a prose
 * question, e.g. a Propose `recommended_question` `{id, category, question, why, when?}` or a
 * `deep_dive_question` `{pillar, question}`). Recognizing the prose shape is what lets the Propose /
 * deep-dive question corpora be referenced and edited from the step Questions panel.
 */
const isQuestionPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return 'ask' in p || 'options' in p || 'who' in p || 'question' in p;
};

/**
 * True for a *banded* question payload (an `ask` prompt plus `options`/cues/plays or a `who`), as
 * opposed to a *prose* question (`question` + `why`, optionally `category`/`when`/`pillar`). Drives
 * which in-place editor a referenced block gets — the banded options/cues editor or the prose editor.
 */
const isBandedPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return 'ask' in p || 'options' in p || 'who' in p;
};

/**
 * Read a step's `metadata.questionRefs` into the form shape (tolerant of missing/typed-wrong fields).
 * Mirrors `readStepFiles`; keeps unknown/stale category+key rows visible rather than dropping them.
 */
export const readStepQuestionRefs = (raw: unknown): StepQuestionRef[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r) => r && typeof r === 'object')
    .map((r) => {
      const o = r as Record<string, unknown>;
      return {
        slot: typeof o.slot === 'string' ? o.slot : '',
        category: typeof o.category === 'string' ? o.category : '',
        key: typeof o.key === 'string' ? o.key : '',
      };
    });
};

/** The question references worth persisting — a row needs both a category and a picked question key. */
export const cleanStepQuestionRefs = (
  refs: StepQuestionRef[] | undefined,
): StepQuestionRef[] =>
  (refs ?? [])
    .map((r) => {
      const slot = (r.slot ?? '').trim();
      return {
        ...(slot ? { slot } : {}),
        category: (r.category ?? '').trim(),
        key: (r.key ?? '').trim(),
      };
    })
    .filter((r) => r.category && r.key);

/**
 * Structured options/cues editor for one referenced question block's `payload`. Edits the block's
 * `ask`/`why` text and its `CampaignQuestionOption[]` (`value` + one-per-line `cues` + one-per-line
 * `plays`); persists the FULL block record via `onSave` (a full-record PUT). Local draft state is
 * reseeded whenever the server payload changes, and commits compare JSON to avoid redundant writes.
 */
export function QuestionBlockEditor(props: {
  block: CampaignContentBlock;
  campaignId?: number;
  disabled?: boolean;
  onSave: (block: CampaignContentBlock, payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { block, campaignId, disabled, onSave } = props;
  const payload = (block.payload ?? {}) as CampaignContentBlockPayload;

  // Capability options for the per-option `plays` picker (each play references a CampaignCapability by
  // key), so a play can't drift to a typo'd key that resolves to nothing in the runner.
  const { capabilityOptions, createCapability } = useCampaignCapabilityReference(campaignId);

  const serverAsk = typeof payload.ask === 'string' ? payload.ask : '';
  const serverWhy = typeof payload.why === 'string' ? payload.why : '';
  const serverOptions: CampaignQuestionOption[] = React.useMemo(
    () => (Array.isArray(payload.options) ? (payload.options as CampaignQuestionOption[]) : []),
    [payload.options],
  );

  const signature = React.useMemo(
    () => JSON.stringify({ ask: serverAsk, why: serverWhy, options: serverOptions }),
    [serverAsk, serverWhy, serverOptions],
  );

  const [ask, setAsk] = React.useState(serverAsk);
  const [why, setWhy] = React.useState(serverWhy);
  const [options, setOptions] = React.useState<CampaignQuestionOption[]>(serverOptions);

  // Reseed the draft when the underlying block changes (id) or the server value is refreshed.
  React.useEffect(() => {
    setAsk(serverAsk);
    setWhy(serverWhy);
    setOptions(serverOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, signature]);

  const commit = (next: { ask: string; why: string; options: CampaignQuestionOption[] }) => {
    const nextPayload: CampaignContentBlockPayload = {
      ...payload,
      ask: next.ask,
      why: next.why,
      options: next.options,
    };
    if (JSON.stringify({ ask: next.ask, why: next.why, options: next.options }) === signature) return;
    onSave(block, nextPayload);
  };

  const updateOption = (index: number, patch: Partial<CampaignQuestionOption>) => {
    const next = options.map((o, i) => (i === index ? { ...o, ...patch } : o));
    setOptions(next);
    return next;
  };

  const addOption = () => {
    const next = [...options, { value: '', cues: [], plays: [] }];
    setOptions(next);
    commit({ ask, why, options: next });
  };

  const removeOption = (index: number) => {
    const next = options.filter((_, i) => i !== index);
    setOptions(next);
    commit({ ask, why, options: next });
  };

  return (
    <Stack spacing={1.25} sx={{ pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography variant="caption" color="text.secondary">
        Edit this question&apos;s prompt and answer bands. Changes save to the shared question block.
      </Typography>

      <TextField
        label="Ask"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={ask}
        disabled={disabled}
        onChange={(e) => setAsk(e.target.value)}
        onBlur={() => commit({ ask, why, options })}
        helperText="The question prompt (payload.ask)"
      />
      <TextField
        label="Why (optional)"
        size="small"
        fullWidth
        multiline
        minRows={1}
        value={why}
        disabled={disabled}
        onChange={(e) => setWhy(e.target.value)}
        onBlur={() => commit({ ask, why, options })}
        helperText="Why this question matters (payload.why)"
      />

      <Typography variant="caption" color="text.secondary">
        Answer options / cues
      </Typography>
      {options.length === 0 && (
        <Typography variant="caption" color="text.disabled">
          No options yet.
        </Typography>
      )}
      {options.map((opt, index) => (
        <Box
          key={index}
          sx={{ p: 1, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Value"
                size="small"
                fullWidth
                value={opt.value ?? ''}
                disabled={disabled}
                onChange={(e) => updateOption(index, { value: e.target.value })}
                onBlur={() => commit({ ask, why, options })}
                helperText="The band/value the SE selects (e.g. gap)"
              />
              {!disabled && (
                <Tooltip title="Remove option">
                  <IconButton size="small" onClick={() => removeOption(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Cues (one per line)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                defaultValue={arrToLines(opt.cues)}
                disabled={disabled}
                onBlur={(e) => {
                  const next = updateOption(index, { cues: linesToArr(e.target.value) });
                  commit({ ask, why, options: next });
                }}
              />
              <ReferenceMultiSelect
                label="Plays"
                value={Array.isArray(opt.plays) ? opt.plays : []}
                options={capabilityOptions}
                disabled={disabled}
                helperText="Capabilities this answer proposes"
                onChange={(v) => {
                  const next = updateOption(index, { plays: v });
                  commit({ ask, why, options: next });
                }}
                onCreate={!disabled && campaignId ? createCapability : undefined}
                createLabel="+ New capability"
              />
            </Stack>
          </Stack>
        </Box>
      ))}
      {!disabled && <GhostButton onClick={addOption}>+ Add option</GhostButton>}
    </Stack>
  );
}

/**
 * In-place editor for a *prose* question block's `payload` — the Propose `recommended_question`
 * (`{id, category, question, why, when?}`) and `deep_dive_question` (`{pillar, question}`) shapes,
 * which carry a `question` prompt rather than an `ask` + answer bands. Edits the `question`/`why`
 * text and, when present on the payload, its display `category`, its optional `when` gate (which
 * Cisco IQ scenario the question shows for), and its `pillar` grouping. Every other field (notably
 * the stable `id` the runner keys on) is preserved untouched. Persists the FULL block record via
 * `onSave` (a full-record PUT), reseeding from the server value and comparing JSON to skip no-op writes.
 */
export function ProseQuestionEditor(props: {
  block: CampaignContentBlock;
  campaignId?: number;
  disabled?: boolean;
  onSave: (block: CampaignContentBlock, payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { block, campaignId, disabled, onSave } = props;
  const payload = (block.payload ?? {}) as CampaignContentBlockPayload & Record<string, unknown>;

  // The `when` gate references a Cisco IQ scenario by id; offer the campaign's authored `iq_scenario`
  // blocks (falling back to the built-in states) so the gate can't point at a scenario that isn't there.
  const { data: iqBlocks } = useCampaignContentBlocks(campaignId ?? 0, 'iq_scenario');
  const iqMutations = useCampaignMutations('contentBlock');
  const iqOptions: ReferenceOption[] = React.useMemo(() => {
    const derived = (iqBlocks ?? [])
      .map((b) => {
        const p = (b.payload ?? {}) as Record<string, unknown>;
        const value = String((typeof p.id === 'string' && p.id) || b.key || '');
        const rawLabel = typeof p.label === 'string' && p.label ? p.label : b.label;
        return { value, label: rawLabel || value };
      })
      .filter((o) => o.value);
    return derived.length > 0 ? derived : IQ_SCENARIO_FALLBACK;
  }, [iqBlocks]);
  const createIqScenario = React.useCallback(async (): Promise<string | null> => {
    if (!campaignId) return null;
    const id = `iq-${Date.now().toString(36)}`;
    try {
      await iqMutations.create.mutateAsync({
        campaign: campaignId,
        parent: null,
        category: 'iq_scenario',
        key: id,
        label: '',
        order: 0,
        hidden: false,
        payload: { id },
        metadata: {},
      });
      return id;
    } catch {
      return null;
    }
  }, [campaignId, iqMutations.create]);

  const hasCategory = 'category' in payload;
  const hasWhen = 'when' in payload || hasCategory; // a recommended question may gain a gate it lacked
  const hasPillar = 'pillar' in payload;

  const serverQuestion = typeof payload.question === 'string' ? payload.question : '';
  const serverWhy = typeof payload.why === 'string' ? payload.why : '';
  const serverCategory = typeof payload.category === 'string' ? payload.category : '';
  const serverWhen = typeof payload.when === 'string' ? payload.when : '';
  const serverPillar = typeof payload.pillar === 'string' ? payload.pillar : '';

  const signature = React.useMemo(
    () =>
      JSON.stringify({
        question: serverQuestion,
        why: serverWhy,
        category: serverCategory,
        when: serverWhen,
        pillar: serverPillar,
      }),
    [serverQuestion, serverWhy, serverCategory, serverWhen, serverPillar],
  );

  const [question, setQuestion] = React.useState(serverQuestion);
  const [why, setWhy] = React.useState(serverWhy);
  const [category, setCategory] = React.useState(serverCategory);
  const [when, setWhen] = React.useState(serverWhen);
  const [pillar, setPillar] = React.useState(serverPillar);

  React.useEffect(() => {
    setQuestion(serverQuestion);
    setWhy(serverWhy);
    setCategory(serverCategory);
    setWhen(serverWhen);
    setPillar(serverPillar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, signature]);

  const commit = (next: {
    question: string;
    why: string;
    category: string;
    when: string;
    pillar: string;
  }) => {
    if (JSON.stringify(next) === signature) return;
    // Preserve every unrelated field (notably the stable `id`); write only the edited keys, and drop
    // an emptied optional gate rather than persisting an empty `when` that would gate on nothing.
    const nextPayload: CampaignContentBlockPayload = {
      ...payload,
      question: next.question,
      why: next.why,
      ...(hasCategory ? { category: next.category } : {}),
      ...(hasPillar ? { pillar: next.pillar } : {}),
    } as CampaignContentBlockPayload;
    if (hasWhen) {
      if (next.when.trim()) (nextPayload as Record<string, unknown>).when = next.when.trim();
      else delete (nextPayload as Record<string, unknown>).when;
    }
    onSave(block, nextPayload);
  };

  const current = { question, why, category, when, pillar };

  return (
    <Stack spacing={1.25} sx={{ pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography variant="caption" color="text.secondary">
        Edit this question&apos;s prompt and guidance. Changes save to the shared question block.
      </Typography>

      <TextField
        label="Question"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={question}
        disabled={disabled}
        onChange={(e) => setQuestion(e.target.value)}
        onBlur={() => commit(current)}
        helperText="The question prompt shown to the SE (payload.question)"
      />
      <TextField
        label="Why (optional)"
        size="small"
        fullWidth
        multiline
        minRows={1}
        value={why}
        disabled={disabled}
        onChange={(e) => setWhy(e.target.value)}
        onBlur={() => commit(current)}
        helperText="Why this question matters (payload.why)"
      />
      {hasCategory && (
        <TextField
          label="Group (optional)"
          size="small"
          fullWidth
          value={category}
          disabled={disabled}
          onChange={(e) => setCategory(e.target.value)}
          onBlur={() => commit(current)}
          helperText="The heading this question is grouped under (payload.category, e.g. Inventory)"
        />
      )}
      {hasWhen && (
        <ReferenceSelect
          label="Show only for Cisco IQ scenario (optional)"
          value={when}
          options={iqOptions}
          disabled={disabled}
          emptyLabel="Always show"
          helperText="Leave blank to always show. Otherwise the Cisco IQ scenario that gates it (payload.when)."
          onChange={(v) => {
            setWhen(v);
            commit({ ...current, when: v });
          }}
          onCreate={!disabled && campaignId ? createIqScenario : undefined}
          createLabel="+ New scenario"
          fullWidth
        />
      )}
      {hasPillar && (
        <TextField
          label="Pillar (optional)"
          size="small"
          fullWidth
          value={pillar}
          disabled={disabled}
          onChange={(e) => setPillar(e.target.value)}
          onBlur={() => commit(current)}
          helperText="The pillar this deep-dive question belongs to (payload.pillar)"
        />
      )}
    </Stack>
  );
}

/**
 * Dispatch a referenced question block to the right in-place editor: the banded options/cues editor
 * for an `ask`/`options`/`who` payload, or the prose editor for a `question`-shaped payload (Propose
 * `recommended_question` / `deep_dive_question`). So any referenced question is editable in place,
 * with the fields that shape actually uses.
 */
/**
 * Multi-category tags for one question block. The structural `category` is the block's home category
 * (always a member, shown as a fixed chip); `payload.categories` holds ADDITIONAL memberships so a
 * single question can also live in Discovery / FAQ / Handling Pushback etc. without duplicating the row
 * (the runtime `resolveContentBlocks` matches either). Admins add an existing category from the dropdown
 * or type a brand-new one — categories are free strings, so a new one needs no schema/migration.
 * Persists the full block via `onSave` (a full-record PUT into `payload.categories`).
 */
function QuestionCategoriesField(props: {
  block: CampaignContentBlock;
  availableCategories: string[];
  disabled?: boolean;
  onSave: (block: CampaignContentBlock, payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { block, availableCategories, disabled, onSave } = props;
  const [draft, setDraft] = React.useState('');
  const home = (block.category ?? '').trim();
  const extra: string[] = Array.isArray((block.payload as any)?.categories)
    ? ((block.payload as any).categories as unknown[]).filter(
        (c): c is string => typeof c === 'string' && c.trim() !== '' && c.trim() !== home,
      )
    : [];

  const commit = (nextExtra: string[]) => {
    const unique = Array.from(new Set(nextExtra.map((c) => c.trim()).filter(Boolean)));
    onSave(block, { ...(block.payload as CampaignContentBlockPayload), categories: unique });
  };
  const addCategory = (raw: string) => {
    const c = raw.trim();
    if (!c || c === home || extra.includes(c)) return;
    commit([...extra, c]);
    setDraft('');
  };
  const removeCategory = (c: string) => commit(extra.filter((x) => x !== c));

  // Existing categories not already applied to this block, offered in the quick-add dropdown.
  const addable = availableCategories.filter((c) => c && c !== home && !extra.includes(c));

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">
        Categories — this question also appears under these when steps pick questions by category.
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
        {home && <Chip size="small" label={`${home} (home)`} variant="filled" />}
        {extra.map((c) => (
          <Chip
            key={c}
            size="small"
            label={c}
            variant="outlined"
            onDelete={disabled ? undefined : () => removeCategory(c)}
          />
        ))}
        {!extra.length && !home && (
          <Typography variant="caption" color="text.disabled">
            No categories yet.
          </Typography>
        )}
      </Stack>
      {!disabled && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select={addable.length > 0}
            size="small"
            label="Add category"
            value={draft}
            onChange={(e) => {
              const v = e.target.value;
              // A dropdown pick adds immediately; free text waits for the Add button (so a new
              // category name can be typed in full).
              if (addable.length > 0 && addable.includes(v)) addCategory(v);
              else setDraft(v);
            }}
            sx={{ minWidth: 200 }}
            helperText={addable.length > 0 ? 'Pick an existing category or type a new one below.' : undefined}
          >
            {addable.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          {addable.length > 0 && (
            <TextField
              size="small"
              label="New category"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCategory(draft);
                }
              }}
              sx={{ minWidth: 180 }}
            />
          )}
          <Button size="small" variant="outlined" onClick={() => addCategory(draft)} disabled={!draft.trim()}>
            Add
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function ReferencedQuestionEditor(props: {
  block: CampaignContentBlock;
  campaignId?: number;
  availableCategories?: string[];
  disabled?: boolean;
  onSave: (block: CampaignContentBlock, payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { availableCategories, ...editorProps } = props;
  return (
    <Stack spacing={1.25}>
      <QuestionCategoriesField
        block={props.block}
        availableCategories={availableCategories ?? []}
        disabled={props.disabled}
        onSave={props.onSave}
      />
      {isBandedPayload(props.block.payload) ? (
        <QuestionBlockEditor {...editorProps} />
      ) : (
        <ProseQuestionEditor {...editorProps} />
      )}
    </Stack>
  );
}

/** A fresh, empty payload appropriate to the category a new question is created in. */
const newQuestionPayload = (category: string, key: string): CampaignContentBlockPayload => {
  if (category === 'recommended_question') {
    return { id: key, category: '', question: '', why: '' } as CampaignContentBlockPayload;
  }
  if (category === 'deep_dive_question') {
    return { pillar: '', question: '' } as CampaignContentBlockPayload;
  }
  return { ask: '', why: '', options: [] };
};

/**
 * Generic "Questions" authoring panel for a step. Mirrors `StepFilesFields`: a step declares which of
 * the campaign's predefined question blocks fill named roles (stored on `step.metadata.questionRefs` as
 * `StepQuestionRef[]`), and the author repoints/adds/removes references and structurally edits the
 * referenced blocks' options/cues in place. Purely controlled: binds the `questionRefs` array on the
 * parent form's `control`.
 */
export function StepQuestionsFields(props: {
  control: Control<any>;
  campaignId: number;
  disabled?: boolean;
  /** Override the panel header (defaults to "Questions"). */
  title?: string;
  /** Optional clarifying note rendered as an info banner under the header (e.g. export-only scope). */
  note?: string;
}): JSX.Element {
  const { control, campaignId, disabled, title, note } = props;
  const { data: allBlocks = [] } = useCampaignContentBlocks(campaignId);
  const blockMut = useCampaignMutations('contentBlock');

  // Every category a block belongs to: its structural `category` plus any additional memberships in
  // `payload.categories` (a string[]). A single question can therefore live in several categories
  // (Discovery + FAQ + Handling Pushback) without duplicating the row — the same shape the runtime
  // resolver honors in `resolveContentBlocks`.
  const categoriesOf = React.useCallback((b: CampaignContentBlock): string[] => {
    const cats = new Set<string>();
    if (b.category) cats.add(b.category);
    const extra = (b.payload as { categories?: unknown } | undefined)?.categories;
    if (Array.isArray(extra)) extra.forEach((c) => typeof c === 'string' && c.trim() && cats.add(c));
    return Array.from(cats);
  }, []);

  // Question categories: those whose blocks carry a question-shaped payload (ask/options/who). If none
  // look question-shaped (e.g. an empty/odd campaign), offer every category rather than nothing.
  const questionCategories = React.useMemo(() => {
    const qCats = new Set<string>();
    const allCats = new Set<string>();
    for (const b of allBlocks) {
      const cats = categoriesOf(b);
      if (!cats.length) continue;
      cats.forEach((c) => allCats.add(c));
      if (isQuestionPayload(b.payload)) cats.forEach((c) => qCats.add(c));
    }
    const list = Array.from(qCats.size ? qCats : allCats);
    return list.sort();
  }, [allBlocks, categoriesOf]);

  const blocksByCategory = React.useMemo(() => {
    const m = new Map<string, CampaignContentBlock[]>();
    for (const b of allBlocks) {
      for (const cat of categoriesOf(b)) {
        const arr = m.get(cat) ?? [];
        arr.push(b);
        m.set(cat, arr);
      }
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);
    return m;
  }, [allBlocks, categoriesOf]);

  const blockOf = (category: string, key: string): CampaignContentBlock | undefined =>
    (blocksByCategory.get(category) ?? []).find((b) => b.key === key);

  const onSaveBlock = (block: CampaignContentBlock, payload: CampaignContentBlockPayload) =>
    blockMut.update.mutate({ ...block, payload, id: block.id });

  return (
    <Stack spacing={1.5} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title ?? 'Questions'}
      </Typography>
      {note && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
          {note}
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        Reference this step&apos;s predefined questions from the campaign&apos;s question blocks. Pick an existing
        question or create a new one inline, repoint a reference to a different question, and edit the
        chosen question&apos;s prompt and answer options/cues in place — edits save to the shared question
        block, not just this step.
      </Typography>

      <Controller
        name="questionRefs"
        control={control}
        render={({ field }) => {
          const refs: StepQuestionRef[] = Array.isArray(field.value) ? field.value : [];
          // Slot roles already used on this step — offered as freeSolo suggestions so a role is reused
          // (not re-typed with a typo), while a brand-new role is still allowed.
          const usedSlots = Array.from(
            new Set(refs.map((r) => (r.slot ?? '').trim()).filter(Boolean)),
          );

          const update = (index: number, patch: Partial<StepQuestionRef>) =>
            field.onChange(refs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
          const removeAt = (index: number) =>
            field.onChange(refs.filter((_, i) => i !== index));
          const add = () =>
            field.onChange([
              ...refs,
              { slot: '', category: questionCategories[0] ?? '', key: '' },
            ]);

          // Create a brand-new question block (with a stable key + empty payload) in the chosen (or
          // first) question category AND append a reference row pointing at it — mirrors
          // `createAndReference` in StepFilesFields. The create invalidates the block list, so the new
          // block resolves into the row's editor once it refetches, ready to author.
          const createAndReference = async () => {
            const category = questionCategories[0] ?? 'question_bank';
            const key = `question-${Date.now().toString(36)}`;
            const categoryRows = blocksByCategory.get(category) ?? [];
            try {
              await blockMut.create.mutateAsync({
                campaign: campaignId,
                parent: null,
                category,
                key,
                label: '',
                order: nextOrderOf(categoryRows),
                hidden: false,
                payload: newQuestionPayload(category, key),
                metadata: {},
              });
              field.onChange([...refs, { slot: '', category, key }]);
            } catch {
              /* the mutation surfaces its own error state; leave the form unchanged */
            }
          };

          return (
            <Stack spacing={1.5}>
              {refs.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No question references yet.
                </Typography>
              )}

              {refs.map((row, index) => {
                const categoryRows = blocksByCategory.get(row.category) ?? [];
                const block = row.category && row.key ? blockOf(row.category, row.key) : undefined;
                return (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Stack spacing={1.25} sx={{ flex: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <SlotAutocomplete
                          options={usedSlots}
                          value={row.slot ?? ''}
                          disabled={disabled}
                          onChange={(v) => update(index, { slot: v })}
                          helperText="Optional role for this question (e.g. opener)"
                        />
                        <TextField
                          select
                          label="Category"
                          size="small"
                          value={row.category}
                          disabled={disabled}
                          helperText={
                            questionCategories.length
                              ? 'The question family'
                              : 'No question blocks yet — add one in the Content Blocks section.'
                          }
                          onChange={(e) =>
                            update(index, { category: e.target.value, key: '' })
                          }
                          sx={{ minWidth: 200 }}
                        >
                          {/* Preserve an unknown/stale category so it is not silently dropped. */}
                          {row.category && !questionCategories.includes(row.category) && (
                            <MenuItem value={row.category}>{row.category} (missing)</MenuItem>
                          )}
                          {questionCategories.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Question"
                          size="small"
                          fullWidth
                          value={row.key}
                          disabled={disabled || !row.category}
                          helperText={
                            categoryRows.length
                              ? 'Pick a question by key'
                              : 'No questions in this category yet.'
                          }
                          onChange={(e) => update(index, { key: e.target.value })}
                        >
                          {/* Preserve an unknown/stale key so it is not silently dropped. */}
                          {row.key && !blockOf(row.category, row.key) && (
                            <MenuItem value={row.key}>{row.key} (missing)</MenuItem>
                          )}
                          {categoryRows.map((b) => (
                            <MenuItem key={b.id} value={b.key}>
                              {b.label || b.key || '(untitled)'}
                              {b.key ? ` · ${b.key}` : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      {block ? (
                        <ReferencedQuestionEditor
                          block={block}
                          campaignId={campaignId}
                          availableCategories={questionCategories}
                          disabled={disabled}
                          onSave={onSaveBlock}
                        />
                      ) : row.key ? (
                        <Typography variant="caption" color="warning.main">
                          No question with key “{row.key}” in {row.category}. Add it in the Content
                          Blocks section.
                        </Typography>
                      ) : null}
                    </Stack>

                    {!disabled && (
                      <Tooltip title="Remove question reference">
                        <IconButton size="small" onClick={() => removeAt(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}

              {!disabled && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <GhostButton onClick={add}>+ Add question reference</GhostButton>
                  <GhostButton onClick={createAndReference} disabled={blockMut.isSaving}>
                    {blockMut.isSaving ? 'Creating…' : '+ New question & reference'}
                  </GhostButton>
                </Stack>
              )}
            </Stack>
          );
        }}
      />
    </Stack>
  );
}

export default StepQuestionsFields;
